/**
 * Answers, on demand, "does the server still report this project's planning
 * step as running?" from the task list the planning page already polls.
 *
 * A running planning task mirrors the server's pipeline step (step `running`
 * is task `ai_in_progress`), which is the same state that makes the server
 * refuse a Retry. The answer is read from the RTK Query cache at call time,
 * so it costs no request and no re-render.
 */
import { useCallback } from 'react';
import { useStore } from 'react-redux';

import type { Task } from '../services/taskService';
import type { RootState } from '../store';
import { tasksApi } from '../store/services/tasksApi';
import { isRunningPlanningTask } from '../utils/planningTaskPresentation';

/**
 * How recent the task list must be for its running state to count. The page
 * polls every few seconds while a planning task runs, so an older list means
 * the polls have been failing or stopped, and it proves nothing.
 */
export const PLANNING_TASKS_FRESH_MS = 60_000;

interface TaskListSnapshot {
  data?: Task[];
  fulfilledTimeStamp?: number;
}

export const reportsRunningPlanningStep = (snapshot: TaskListSnapshot, now: number): boolean => (
  typeof snapshot.fulfilledTimeStamp === 'number'
  && now - snapshot.fulfilledTimeStamp <= PLANNING_TASKS_FRESH_MS
  && Boolean(snapshot.data?.some(isRunningPlanningTask))
);

export const usePlanningRunActiveProbe = (projectId: string | null): (() => boolean) => {
  const store = useStore<RootState>();

  return useCallback(() => Boolean(projectId) && reportsRunningPlanningStep(
    tasksApi.endpoints.getTasks.select(projectId ?? '')(store.getState()),
    Date.now(),
  ), [projectId, store]);
};
